import React, { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Syringe, Plus, AlertCircle, X, History, Search, CalendarDays, CalendarPlus, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCalendarVaccines, getAppointments, updateAppointmentStatus, rescheduleVaccine, rescheduleAppointment, changeVaccineStatus, getBookingRequestsCount, getBookingCalendar, changeBookingTreatmentStatus, rescheduleBooking } from '../services/api';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';
import StatusDropdown from '../components/calendar/StatusDropdown';
import DateChangeModal from '../components/calendar/DateChangeModal';
import NewAppointmentModal from '../components/calendar/NewAppointmentModal';
import StatusConfirmModal from '../components/calendar/StatusConfirmModal';
import StatusHistoryModal from '../components/calendar/StatusHistoryModal';
import BookingRequestsModal from '../components/calendar/BookingRequestsModal';
import DirectBookingModal from '../components/calendar/DirectBookingModal';
import QuickOperationModal from '../components/QuickOperationModal';
import { toast } from 'sonner';

const TABS = [
  { key: 'vaccine', label: 'Aşı Takvimi ve Randevular', icon: Syringe },
];

const DAY_NAMES = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const fmtDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fmtDayNum = (d) => d.getDate();
const isToday = (d) => fmtDate(d) === fmtDate(new Date());

const getWeekStart = (date) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const getDays = (start, count) => {
  const days = [];
  for (let i = 0; i < count; i++) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return days;
};

const getWeekLabel = (currentDate, viewMode) => {
  if (viewMode !== 'week') return null;
  const now = new Date();
  const thisWeekStart = getWeekStart(now);
  const viewWeekStart = getWeekStart(currentDate);
  const diffDays = Math.round((viewWeekStart - thisWeekStart) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Bu Hafta';
  if (diffDays === 7) return 'Gelecek Hafta';
  if (diffDays === -7) return 'Geçen Hafta';
  return null;
};

const DEFAULT_ROWS = 10;
const MONTH_CELL_MAX = 2;

// Build monthly grid: array of weeks, each week has 7 day-objects
const buildMonthGrid = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const totalDays = lastDay.getDate();
  // Monday=0, Sunday=6
  let startDow = (firstDay.getDay() + 6) % 7;
  const grid = [];
  let week = [];
  // Padding from previous month
  for (let i = startDow - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    week.push({ date: d, isCurrentMonth: false });
  }
  // Current month days
  for (let d = 1; d <= totalDays; d++) {
    week.push({ date: new Date(year, month, d), isCurrentMonth: true });
    if (week.length === 7) { grid.push(week); week = []; }
  }
  // Padding from next month
  if (week.length > 0) {
    let nextDay = 1;
    while (week.length < 7) {
      week.push({ date: new Date(year, month + 1, nextDay++), isCurrentMonth: false });
    }
    grid.push(week);
  }
  return grid;
};

export default function Calendar() {
  const tab = 'vaccine';
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [vaccineData, setVaccineData] = useState([]);
  const [appointmentData, setAppointmentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateChangeTarget, setDateChangeTarget] = useState(null);
  const [opModal, setOpModal] = useState({ open: false, customer: null, pet: null, appointmentId: null });
  const [confirmModal, setConfirmModal] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [directBookingOpen, setDirectBookingOpen] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const [bookingData, setBookingData] = useState([]);

  const rangeStart = viewMode === 'week' ? getWeekStart(currentDate) : getMonthStart(currentDate);
  const dayCount = viewMode === 'week' ? 7 : new Date(rangeStart.getFullYear(), rangeStart.getMonth() + 1, 0).getDate();
  const days = getDays(rangeStart, dayCount);

  // For monthly view, extend range to cover grid padding days
  const monthGrid = viewMode === 'month' ? buildMonthGrid(currentDate.getFullYear(), currentDate.getMonth()) : null;
  const startStr = viewMode === 'month' && monthGrid
    ? fmtDate(monthGrid[0][0].date)
    : fmtDate(days[0]);
  const endStr = viewMode === 'month' && monthGrid
    ? fmtDate(monthGrid[monthGrid.length - 1][6].date)
    : fmtDate(days[days.length - 1]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'vaccine') {
        const [vaccRes, bookRes] = await Promise.all([
          getCalendarVaccines(startStr, endStr),
          getBookingCalendar(startStr, endStr)
        ]);
        setVaccineData(vaccRes.data);
        setBookingData(bookRes.data);
      } else {
        const res = await getAppointments(startStr, endStr);
        setAppointmentData(res.data);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [tab, startStr, endStr]);

  const loadRequestCount = useCallback(async () => {
    try {
      const res = await getBookingRequestsCount();
      setRequestCount(res.data.count);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadData(); loadRequestCount(); }, [loadData, loadRequestCount]);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === 'week') d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
  };

  const goThisWeek = () => setCurrentDate(new Date());

  // Max 2 days back for status changes
  const isWithinEditWindow = (dateStr) => {
    if (!dateStr) return true;
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0,0,0,0);
    const diffDays = (today - d) / (1000 * 60 * 60 * 24);
    return diffDays <= 2;
  };

  // Vaccine status change (handles both vaccines and bookings)
  const handleVaccineStatusChange = async (item, newStatus) => {
    if (!isWithinEditWindow(item.next_due_date || item.date)) {
      toast.error('2 günden eski kayıtların durumu değiştirilemez');
      return;
    }

    // Booking items
    if (item._isBooking) {
      if (item._notApproved) return; // No action on unapproved
      const currentStatus = item.is_completed ? 'completed' : item.is_informed ? 'informed' : 'pending';

      if (currentStatus === 'completed' && newStatus !== 'completed') {
        setConfirmModal({ item, tab: 'vaccine', targetStatus: newStatus });
        return;
      }

      if (newStatus === 'completed') {
        setOpModal({
          open: true,
          customer: { id: item.customer_id, name: item.customer_name },
          pet: { id: item.pet_id, name: item.pet_name, species: item.pet_species },
          bookingId: item.id,
        });
      } else if (newStatus === 'reschedule') {
        setDateChangeTarget({ id: item.id, type: 'booking', currentDate: item.date, currentTime: item.start_time });
      } else {
        try {
          await changeBookingTreatmentStatus(item.id, newStatus);
          toast.success('Randevu durumu güncellendi');
          loadData();
        } catch { toast.error('Hata oluştu'); }
      }
      return;
    }

    // Original vaccine logic
    const currentStatus = item.is_completed ? 'completed' : item.is_informed ? 'informed' : 'pending';

    // If currently completed and changing to something else → show confirmation
    if (currentStatus === 'completed' && newStatus !== 'completed') {
      setConfirmModal({ item, tab: 'vaccine', targetStatus: newStatus });
      return;
    }

    if (newStatus === 'completed') {
      setOpModal({
        open: true,
        customer: { id: item.customer_id, name: item.customer_name },
        pet: { id: item.pet_id, name: item.pet_name, species: item.pet_species },
        vaccineOperationId: item.id,
      });
    } else if (newStatus === 'reschedule') {
      setDateChangeTarget({ id: item.id, type: 'vaccine', currentDate: item.next_due_date });
    } else {
      try {
        await changeVaccineStatus(item.id, newStatus);
        toast.success('Aşı durumu güncellendi');
        loadData();
      } catch { toast.error('Hata oluştu'); }
    }
  };

  // Appointment status change
  const handleAppointmentStatusChange = async (item, newStatus) => {
    if (!isWithinEditWindow(item.date)) {
      toast.error('2 günden eski kayıtların durumu değiştirilemez');
      return;
    }
    const currentStatus = item.status || 'pending';

    // If currently completed and changing to something else → show confirmation
    if (currentStatus === 'completed' && newStatus !== 'completed') {
      setConfirmModal({ item, tab: 'appointment', targetStatus: newStatus });
      return;
    }

    if (newStatus === 'completed') {
      setOpModal({
        open: true,
        customer: { id: item.customer_id, name: item.customer_name },
        pet: item.pet_id ? { id: item.pet_id, name: item.pet_name, species: item.pet_species } : null,
        appointmentId: item.id,
      });
    } else if (newStatus === 'reschedule') {
      setDateChangeTarget({ id: item.id, type: 'appointment', currentDate: item.date, currentTime: item.time });
    } else {
      try {
        await updateAppointmentStatus(item.id, newStatus);
        toast.success('Randevu durumu güncellendi');
        loadData();
      } catch { toast.error('Hata oluştu'); }
    }
  };

  // Confirmation modal handler
  const handleConfirmStatusChange = async () => {
    if (!confirmModal) return;
    const { item, tab, targetStatus } = confirmModal;
    setConfirmModal(null);

    try {
      if (item._isBooking) {
        if (targetStatus === 'reschedule') {
          await changeBookingTreatmentStatus(item.id, 'pending');
          setDateChangeTarget({ id: item.id, type: 'booking', currentDate: item.date, currentTime: item.start_time });
        } else {
          await changeBookingTreatmentStatus(item.id, targetStatus);
        }
      } else if (tab === 'vaccine') {
        if (targetStatus === 'reschedule') {
          await changeVaccineStatus(item.id, 'pending');
          setDateChangeTarget({ id: item.id, type: 'vaccine', currentDate: item.next_due_date });
        } else {
          await changeVaccineStatus(item.id, targetStatus);
        }
      } else {
        if (targetStatus === 'reschedule') {
          await updateAppointmentStatus(item.id, 'pending');
          setDateChangeTarget({ id: item.id, type: 'appointment', currentDate: item.date, currentTime: item.time });
        } else {
          await updateAppointmentStatus(item.id, targetStatus);
        }
      }
      toast.success('Durum güncellendi');
      loadData();
    } catch { toast.error('Hata oluştu'); }
  };

  // Date change save
  const handleDateChangeSave = async (newDate, newTime) => {
    if (!dateChangeTarget) return;
    try {
      if (dateChangeTarget.type === 'vaccine') {
        await rescheduleVaccine(dateChangeTarget.id, newDate);
        toast.success('Aşı tarihi güncellendi');
      } else if (dateChangeTarget.type === 'booking') {
        await rescheduleBooking(dateChangeTarget.id, newDate, newTime);
        toast.success('Randevu tarihi güncellendi');
      } else {
        await rescheduleAppointment(dateChangeTarget.id, newDate, newTime);
        toast.success('Randevu tarihi güncellendi');
      }
      setDateChangeTarget(null);
      loadData();
    } catch { toast.error('Tarih güncellenemedi'); }
  };

  // Mark as done complete
  const handleOpModalClose = async (saved) => {
    if (saved) {
      if (opModal.bookingId) {
        try {
          await changeBookingTreatmentStatus(opModal.bookingId, 'completed');
        } catch {}
      }
      if (opModal.appointmentId) {
        try {
          await updateAppointmentStatus(opModal.appointmentId, 'completed');
        } catch {}
      }
      if (opModal.vaccineOperationId) {
        try {
          await changeVaccineStatus(opModal.vaccineOperationId, 'completed');
        } catch {}
      }
    }
    setOpModal({ open: false, customer: null, pet: null, appointmentId: null, vaccineOperationId: null, bookingId: null });
    loadData();
  };

  const handleNewAppointment = () => {
    setSelectedDate(fmtDate(new Date()));
    setShowNewAppointment(true);
  };

  // Group by date
  const groupByDate = (items, dateField) => {
    const map = {};
    items.forEach(item => {
      const d = item[dateField];
      if (!map[d]) map[d] = [];
      map[d].push(item);
    });
    return map;
  };

  // Transform booking data to vaccine-like format and merge
  const transformedBookings = bookingData.map(b => ({
    ...b,
    _isBooking: true,
    _notApproved: b.status === 'pending' || b.status === 'cancel_requested',
    next_due_date: b.date,
    customer_phone: b.customer_phone || '',
    is_completed: !!b.is_completed,
    is_informed: !!b.is_informed,
    operation_date: null,
  }));

  // Filter data
  const filteredVaccineData = [...vaccineData, ...transformedBookings].filter(v => {
    const status = v._notApproved ? 'not_approved'
      : v.is_completed ? 'completed'
      : v.is_informed ? 'informed'
      : 'pending';
    if (statusFilter !== 'all' && status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      if (!v.customer_name?.toLowerCase().includes(q) && !v.pet_name?.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const vaccineByDate = groupByDate(filteredVaccineData, 'next_due_date');
  const appointmentByDate = groupByDate(appointmentData, 'date');
  const totalEntries = tab === 'vaccine' ? filteredVaccineData.length : appointmentData.length;

  const weekLabel = getWeekLabel(currentDate, viewMode);
  const headerLabel = viewMode === 'week'
    ? `${days[0].toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' })} - ${days[days.length - 1].toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}`
    : days[0].toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4" data-testid="calendar-page">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-3 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 shrink-0">Takvim</h1>
            <span className="flex items-center gap-1 bg-[#6b6de3]/10 text-[#6b6de3] rounded-full px-2.5 py-0.5 text-[10px] font-semibold">
              <Syringe size={11} /> Aşı Takvimi ve Randevular
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button onClick={() => setDirectBookingOpen(true)}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-[11px] sm:text-xs font-semibold text-white bg-[#6b6de3] rounded-lg hover:bg-[#5658c4] transition-colors"
              data-testid="direct-booking-btn">
              <CalendarPlus size={14} /> <span className="hidden sm:inline">Yeni</span> Randevu
            </button>
            <button onClick={() => setRequestsOpen(true)}
              className="relative flex items-center gap-1 px-2 sm:px-3 py-1.5 text-[11px] sm:text-xs font-semibold text-[#FF9500] bg-[#FF9500]/10 rounded-lg hover:bg-[#FF9500]/20 transition-colors"
              data-testid="requests-btn">
              <Bell size={14} /> <span className="hidden sm:inline">Talepler</span>
              {requestCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {requestCount}
                </span>
              )}
            </button>
            <button onClick={() => setHistoryOpen(true)}
              className="flex items-center gap-1 px-2 sm:px-3 py-1.5 text-[11px] sm:text-xs font-semibold text-[#6b6de3] bg-[#6b6de3]/10 rounded-lg hover:bg-[#6b6de3]/20 transition-colors"
              data-testid="history-btn">
              <History size={14} /> <span className="hidden sm:inline">{`Geçmiş`}</span>
            </button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Müşteri veya hasta ara..."
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#F2F2F7] border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6b6de3]"
              data-testid="calendar-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="text-xs py-1.5 px-2 bg-[#F2F2F7] border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6b6de3] text-gray-700 font-medium"
            data-testid="calendar-status-filter"
          >
            <option value="all">Tümü</option>
            <option value="not_approved">Onaylanmadı</option>
            <option value="pending">Yapılmadı</option>
            <option value="informed">Arandı</option>
            <option value="completed">Yapıldı</option>
          </select>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between flex-wrap gap-y-2">
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={goThisWeek} className="text-[10px] font-medium text-[#6b6de3] px-2 py-1.5 rounded-lg hover:bg-[#6b6de3]/10" data-testid="cal-today">
              Bugün
            </button>
            <div className="relative">
              <button onClick={() => setDatePickerOpen(!datePickerOpen)}
                className="flex items-center gap-1 text-[10px] font-medium text-gray-600 px-2 py-1.5 rounded-lg hover:bg-gray-100"
                data-testid="cal-goto-date">
                <CalendarDays size={12} /> Tarihe Git
              </button>
              {datePickerOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-lg border border-gray-200 p-3 z-20 flex items-center gap-2" data-testid="date-picker-dropdown">
                  <input
                    type="date"
                    id="goto-date-input"
                    autoFocus
                    className="text-xs px-3 py-2 bg-[#F2F2F7] border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6b6de3]"
                    data-testid="date-picker-input"
                  />
                  <button
                    onClick={() => {
                      const val = document.getElementById('goto-date-input')?.value;
                      if (val) {
                        const [y, m, d] = val.split('-').map(Number);
                        setCurrentDate(new Date(y, m - 1, d));
                      }
                      setDatePickerOpen(false);
                    }}
                    className="text-[10px] font-semibold text-white bg-[#6b6de3] px-3 py-2 rounded-lg hover:bg-[#5658c4] whitespace-nowrap"
                    data-testid="date-picker-go"
                  >
                    Git
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="cal-prev">
              <ChevronLeft size={18} className="text-gray-600" />
            </button>
            <div className="text-center min-w-[200px]">
              {weekLabel && <p className="text-xs font-bold text-[#6b6de3]">{weekLabel}</p>}
              <p className={`font-semibold text-gray-700 ${weekLabel ? 'text-[11px]' : 'text-sm'}`} data-testid="cal-range">{headerLabel}</p>
            </div>
            <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="cal-next">
              <ChevronRight size={18} className="text-gray-600" />
            </button>
          </div>
          <div className="flex gap-1 bg-[#F2F2F7] rounded-lg p-0.5">
            <button onClick={() => setViewMode('week')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${viewMode === 'week' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
              data-testid="view-week">Hafta</button>
            <button onClick={() => setViewMode('month')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all ${viewMode === 'month' ? 'bg-white text-[#6b6de3] shadow-sm' : 'text-[#8E8E93]'}`}
              data-testid="view-month">Ay</button>
          </div>
        </div>
      </header>

      {/* Calendar Content */}
      <div className="p-3 lg:p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : viewMode === 'week' ? (
          /* ===== WEEKLY VIEW ===== */
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[840px] table-fixed" data-testid="calendar-table">
                <thead>
                  <tr className="border-b border-gray-200">
                    {days.map((d, i) => (
                      <th key={i} className={`px-1 py-2.5 text-center border-r border-gray-100 last:border-r-0 ${isToday(d) ? 'bg-[#6b6de3]/8' : ''}`}>
                        <span className={`block text-[10px] font-medium ${isToday(d) ? 'text-[#6b6de3]' : 'text-[#8E8E93]'}`}>
                          {DAY_NAMES[i % 7]}
                        </span>
                        <span className={`text-sm font-bold mt-0.5 ${isToday(d) ? 'inline-flex items-center justify-center text-white bg-[#6b6de3] w-7 h-7 rounded-full' : 'text-gray-800'}`}>
                          {fmtDayNum(d)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {totalEntries === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-16">
                        <AlertCircle size={28} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm text-[#8E8E93]">
                          {tab === 'vaccine' ? 'Bu tarih aralığında aşı kaydı yok' : 'Bu tarih aralığında randevu yok'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    <tr className="align-top">
                      {days.map((d, di) => {
                        const dateStr = fmtDate(d);
                        const items = tab === 'vaccine'
                          ? (vaccineByDate[dateStr] || [])
                          : (appointmentByDate[dateStr] || []);
                        return (
                          <td key={di}
                            className={`px-1 py-1.5 border-r border-gray-100 last:border-r-0 ${isToday(d) ? 'bg-[#6b6de3]/5' : ''}`}
                            style={{ verticalAlign: 'top' }}>
                            <div className="space-y-1">
                              {items.slice(0, DEFAULT_ROWS).map((item, ii) => (
                                <CellCard key={item.id || ii} item={item} tab={tab}
                                  onVaccineStatus={handleVaccineStatusChange}
                                  onAppointmentStatus={handleAppointmentStatusChange} />
                              ))}
                              {items.length > DEFAULT_ROWS && (
                                <p className="text-[9px] text-[#8E8E93] text-center">+{items.length - DEFAULT_ROWS} daha</p>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
              <p className="text-[11px] text-[#8E8E93] text-center">
                {totalEntries} kayıt bulunuyor
              </p>
            </div>
          </div>
        ) : (
          /* ===== MONTHLY VIEW ===== */
          <MonthlyGrid
            currentDate={currentDate}
            tab={tab}
            vaccineByDate={vaccineByDate}
            appointmentByDate={appointmentByDate}
            totalEntries={totalEntries}
            onVaccineStatus={handleVaccineStatusChange}
            onAppointmentStatus={handleAppointmentStatusChange}
          />
        )}
      </div>

      {/* Modals */}
      <NewAppointmentModal
        open={showNewAppointment}
        onClose={() => setShowNewAppointment(false)}
        onCreated={() => { setShowNewAppointment(false); loadData(); }}
        defaultDate={selectedDate}
      />

      <DateChangeModal
        open={!!dateChangeTarget}
        onClose={() => setDateChangeTarget(null)}
        onSave={handleDateChangeSave}
        currentDate={dateChangeTarget?.currentDate}
        currentTime={dateChangeTarget?.currentTime}
        title={dateChangeTarget?.type === 'vaccine' ? 'Aşı Tarihini Değiştir' : 'Randevu Tarihini Değiştir'}
      />

      <QuickOperationModal
        open={opModal.open}
        onClose={handleOpModalClose}
        initialCustomer={opModal.customer}
        initialPet={opModal.pet}
      />

      {confirmModal && (
        <StatusConfirmModal
          targetStatus={confirmModal.targetStatus}
          onConfirm={handleConfirmStatusChange}
          onCancel={() => setConfirmModal(null)}
        />
      )}

      {historyOpen && (
        <StatusHistoryModal onClose={() => setHistoryOpen(false)} />
      )}

      <BookingRequestsModal
        open={requestsOpen}
        onOpenChange={setRequestsOpen}
        onUpdate={() => { loadData(); loadRequestCount(); }}
      />

      <DirectBookingModal
        open={directBookingOpen}
        onOpenChange={setDirectBookingOpen}
        onCreated={() => { loadData(); }}
      />

      <BottomNav />
    </div>
  );
}

// Monthly Grid Component
function MonthlyGrid({ currentDate, tab, vaccineByDate, appointmentByDate, totalEntries, onVaccineStatus, onAppointmentStatus }) {
  const [dayModal, setDayModal] = useState(null);
  const grid = buildMonthGrid(currentDate.getFullYear(), currentDate.getMonth());

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="monthly-grid">
      <div className="overflow-x-auto">
        <div className="min-w-[840px]">
          <div className="grid grid-cols-7 border-b border-gray-200">
            {DAY_NAMES.map((name, i) => (
              <div key={i} className="py-2.5 text-center text-[11px] font-semibold text-[#8E8E93] uppercase tracking-wide">
                {name}
              </div>
            ))}
          </div>

          {grid.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-gray-100 last:border-b-0">
              {week.map((dayObj, di) => {
                const dateStr = fmtDate(dayObj.date);
                const items = tab === 'vaccine'
                  ? (vaccineByDate[dateStr] || [])
                  : (appointmentByDate[dateStr] || []);
                const today = isToday(dayObj.date);

                return (
                  <div key={di}
                    className={`min-h-[110px] lg:min-h-[130px] border-r border-gray-100 last:border-r-0 p-1 lg:p-1.5 transition-colors overflow-hidden
                      ${!dayObj.isCurrentMonth ? 'bg-gray-50/60' : ''}
                      ${today ? 'bg-[#6b6de3]/5' : ''}`}
                    data-testid={`month-cell-${dateStr}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-bold leading-none
                        ${today ? 'inline-flex items-center justify-center text-white bg-[#6b6de3] w-6 h-6 rounded-full' : ''}
                        ${!dayObj.isCurrentMonth ? 'text-gray-300' : today ? '' : 'text-gray-700'}`}>
                        {dayObj.date.getDate()}
                      </span>
                      {items.length > 0 && (
                        <span className={`text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center
                          ${tab === 'vaccine' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                          {items.length}
                        </span>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      {items.slice(0, MONTH_CELL_MAX).map((item, ii) => (
                        <MonthCellItem key={item.id || ii} item={item} tab={tab}
                          onVaccineStatus={onVaccineStatus} onAppointmentStatus={onAppointmentStatus} />
                      ))}
                      {items.length > MONTH_CELL_MAX && (
                        <button onClick={() => setDayModal({ dateStr, date: dayObj.date, items })}
                          className="w-full text-[10px] font-semibold text-[#6b6de3] hover:text-[#5658c4] py-1 rounded-md hover:bg-[#6b6de3]/10 transition-colors"
                          data-testid={`month-more-${dateStr}`}>
                          +{items.length - MONTH_CELL_MAX} daha
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
        <p className="text-[11px] text-[#8E8E93] text-center">
          {totalEntries} kayıt bulunuyor
        </p>
      </div>

      {/* Day detail modal */}
      {dayModal && (
        <DayDetailModal
          date={dayModal.date}
          items={dayModal.items}
          tab={tab}
          onClose={() => setDayModal(null)}
          onVaccineStatus={onVaccineStatus}
          onAppointmentStatus={onAppointmentStatus}
        />
      )}
    </div>
  );
}

// Day Detail Modal - shows full list of events for a day
function DayDetailModal({ date, items, tab, onClose, onVaccineStatus, onAppointmentStatus }) {
  const dayLabel = date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });

  const fmtShortDate = (d) => {
    if (!d) return '';
    const clean = d.split('T')[0];
    const parts = clean.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return d;
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-50 flex sm:inset-0 sm:items-center sm:justify-center sm:p-4" data-testid="day-detail-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-md lg:max-w-3xl h-full sm:h-auto sm:max-h-[75vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900 capitalize">{dayLabel}</h3>
            <p className="text-xs text-[#8E8E93] mt-0.5">{items.length} {tab === 'vaccine' ? 'aşı kaydı' : 'randevu'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors" data-testid="day-modal-close">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Items list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {items.map((item, i) => {
            const currentStatus = tab === 'vaccine'
              ? (item.is_completed ? 'completed' : item.is_informed ? 'informed' : 'pending')
              : (item.status || 'pending');

            const handleChange = (newStatus) => {
              if (tab === 'vaccine') onVaccineStatus(item, newStatus);
              else onAppointmentStatus(item, newStatus);
            };

            const accent = currentStatus === 'completed' ? 'border-l-green-500 bg-green-50/60'
              : currentStatus === 'cancelled' ? 'border-l-gray-400 bg-gray-50/60'
              : currentStatus === 'informed' ? 'border-l-blue-500 bg-blue-50/60'
              : tab === 'vaccine' ? 'border-l-red-400 bg-white' : 'border-l-blue-400 bg-white';

            return (
              <div key={item.id || i}
                className={`rounded-xl border border-gray-200 border-l-[3px] ${accent} px-3 py-2.5 shadow-sm`}
                data-testid={`day-modal-item-${item.id}`}>
                <div className="flex items-center gap-2">
                  <PetAvatar species={item.pet_species} gender={item.pet_gender} size={18} />
                  <div className="flex-1 min-w-0">
                    <a href={`/customer/${item.customer_id}`}
                      className="text-sm font-bold text-gray-900 truncate block hover:text-[#6b6de3] hover:underline transition-colors"
                      onClick={e => { e.stopPropagation(); }}
                      data-testid={`customer-link-modal-${item.id}`}>
                      {item.customer_name}
                    </a>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-gray-600">{item.pet_name}</span>
                      {item.customer_phone && (
                        <a href={`tel:${item.customer_phone}`} className="text-[11px] text-[#6b6de3] font-medium hover:underline">
                          {item.customer_phone}
                        </a>
                      )}
                    </div>
                  </div>
                  <StatusDropdown status={currentStatus} onChange={handleChange} />
                </div>
                {tab === 'vaccine' && item.operation_name && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-[#6b6de3]">{item.operation_name}</span>
                    {item.operation_date && (
                      <span className="text-[10px] text-[#8E8E93]">Son Aşı: {fmtShortDate(item.operation_date)}</span>
                    )}
                  </div>
                )}
                {tab === 'appointment' && (
                  <div className="mt-1.5 flex items-center gap-2">
                    {item.time && <span className="text-[11px] font-semibold text-[#6b6de3]">{item.time}</span>}
                    {item.note && <span className="text-[11px] text-[#8E8E93] truncate">{item.note}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Compact item for monthly view cells
function MonthCellItem({ item, tab, onVaccineStatus, onAppointmentStatus }) {
  const currentStatus = item._notApproved ? 'not_approved'
    : item.is_completed ? 'completed'
    : item.is_informed ? 'informed'
    : 'pending';

  const handleChange = (newStatus) => {
    if (item._notApproved) return;
    onVaccineStatus(item, newStatus);
  };

  const accent = currentStatus === 'not_approved' ? 'border-l-orange-400 bg-orange-50/60'
    : currentStatus === 'completed' ? 'border-l-green-500 bg-green-50/60'
    : currentStatus === 'cancelled' ? 'border-l-gray-400 bg-gray-50/60'
    : currentStatus === 'informed' ? 'border-l-blue-500 bg-blue-50/60'
    : 'border-l-red-400 bg-white';

  return (
    <div
      className={`group rounded-lg border border-gray-200 border-l-[3px] ${accent} px-1.5 py-1 text-left hover:shadow-sm transition-shadow overflow-hidden max-w-full`}
      data-testid={`month-item-${item.id}`}
    >
      <div className="flex items-center gap-1 min-w-0">
        <PetAvatar species={item.pet_species} gender={item.pet_gender} size={12} />
        <a href={`/customer/${item.customer_id}`}
          className="text-[10px] font-bold text-gray-900 truncate flex-1 min-w-0 leading-tight hover:text-[#6b6de3] hover:underline transition-colors"
          onClick={e => { e.stopPropagation(); }}
          data-testid={`customer-link-${item.id}`}>
          {item.customer_name}
        </a>
        <div className="shrink-0">
          <StatusDropdown status={currentStatus} onChange={handleChange} compact />
        </div>
      </div>
      <div className="flex items-center gap-1 mt-0.5 min-w-0">
        <span className="text-[9px] text-gray-600 truncate min-w-0">{item.pet_name}</span>
        {item.operation_name && (
          <span className="text-[9px] font-semibold text-[#6b6de3] truncate min-w-0">- {item.operation_name}</span>
        )}
      </div>
      {item._isBooking && item.start_time && (
        <div className="flex items-center gap-1 mt-0.5 min-w-0">
          <span className="text-[9px] font-bold text-[#6b6de3] bg-[#6b6de3]/10 px-1 py-0.5 rounded truncate">{item.start_time}-{item.end_time}</span>
        </div>
      )}
      {!item._isBooking && item.customer_phone && (
        <div className="flex items-center gap-1 mt-0.5 min-w-0">
          <a href={`tel:${item.customer_phone}`} className="text-[9px] text-[#6b6de3] font-medium hover:underline truncate">
            {item.customer_phone}
          </a>
        </div>
      )}
    </div>
  );
}

// Cell card in calendar (weekly view)
function CellCard({ item, tab, onVaccineStatus, onAppointmentStatus }) {
  const currentStatus = item._notApproved ? 'not_approved'
    : item.is_completed ? 'completed'
    : item.is_informed ? 'informed'
    : (tab === 'vaccine' ? 'pending' : (item.status || 'pending'));

  const handleChange = (newStatus) => {
    if (item._notApproved) return;
    if (tab === 'vaccine' || item._isBooking) onVaccineStatus(item, newStatus);
    else onAppointmentStatus(item, newStatus);
  };

  const statusColor = currentStatus === 'not_approved' ? '#f97316'
    : currentStatus === 'completed' ? '#22c55e'
    : currentStatus === 'cancelled' ? '#9ca3af'
    : currentStatus === 'informed' ? '#3b82f6' : '#ef4444';
  const bgClass = currentStatus === 'not_approved' ? 'bg-orange-50'
    : currentStatus === 'completed' ? 'bg-green-50'
    : currentStatus === 'cancelled' ? 'bg-gray-50'
    : currentStatus === 'informed' ? 'bg-blue-50' : 'bg-white';

  return (
    <div className={`${bgClass} rounded-lg border border-gray-100 px-1.5 py-1.5 hover:border-gray-200 transition-all overflow-hidden`}
      style={{ borderLeft: `3px solid ${statusColor}` }}
      data-testid={`cell-${item.id}`}>

      {/* Row 1: Avatar + Name + Status */}
      <div className="flex items-center gap-1 min-w-0">
        <PetAvatar species={item.pet_species} gender={item.pet_gender} size={10} />
        <a href={`/customer/${item.customer_id}`}
          className="text-[11px] font-bold text-gray-900 truncate flex-1 min-w-0 hover:text-[#6b6de3] hover:underline transition-colors"
          onClick={e => { e.stopPropagation(); }}
          data-testid={`customer-link-${item.id}`}>
          {item.customer_name}
        </a>
        <StatusDropdown status={currentStatus} onChange={handleChange} compact />
      </div>

      {/* Row 2: Pet + Phone */}
      <div className="flex items-center gap-1 mt-0.5 min-w-0">
        <span className="text-[10px] text-gray-500 truncate flex-1 min-w-0">{item.pet_name}</span>
        {item.customer_phone && (
          <a href={`tel:${item.customer_phone}`} className="text-[9px] text-[#6b6de3] font-medium hover:underline shrink-0" data-testid={`call-${item.id}`}>
            {item.customer_phone}
          </a>
        )}
      </div>

      {/* Row 3: Operation + time/date */}
      <div className="flex items-center gap-1 mt-0.5 min-w-0">
        {item._isBooking && item.start_time ? (
          <>
            <span className="text-[10px] font-bold text-[#6b6de3] bg-[#6b6de3]/10 px-1.5 py-0.5 rounded shrink-0">{item.start_time}-{item.end_time}</span>
            {item.operation_name && <span className="text-[10px] text-[#8E8E93] truncate min-w-0">{item.operation_name}</span>}
          </>
        ) : tab === 'vaccine' ? (
          <>
            {item.operation_name && <span className="text-[10px] font-bold text-[#6b6de3] bg-[#6b6de3]/10 px-1.5 py-0.5 rounded truncate min-w-0">{item.operation_name}</span>}
          </>
        ) : (
          <>
            {item.time && <span className="text-[10px] font-bold text-[#6b6de3] bg-[#6b6de3]/10 px-1.5 py-0.5 rounded shrink-0">{item.time}</span>}
            {item.note && <span className="text-[10px] text-[#8E8E93] truncate min-w-0">{item.note}</span>}
          </>
        )}
      </div>
    </div>
  );
}


// Status filter now includes 'not_approved'
